import React from 'react';
import PropTypes from 'prop-types';
import { Box, Popover, Divider } from '@eatclubapp/ec-component-library';
import { useMediaQuery, useTheme } from '@mui/material';
import RightArrowSVG from '../../../../assets/icon_small_arrow_right.svg';
import LightbulbSVG from '../../../../assets/images/graphic_lightbulb.svg';
import SettlementSVG from '../../../../assets/images/graphic_settlement.svg';
import useStyles from './CreditPopoverStyles';
import InfoTooltip from '../../../Tooltip/InfoTooltip';
import PopoverTooltip from '../../../Tooltip/PopoverTooltip';
import { formatCurrency } from '../../../../utils/helpers';

const CreditPopover = ({
  isOpen,
  onClose,
  anchorEl,
  onMarketBusinessClick,
  onApplyCreditClick,
  onCreditHistoryClick,
  availableCredit,
  accruedCredit,
  pendingPayoutRequests = [],
  netBalance,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const breakpointMdDown = useMediaQuery(theme.breakpoints.down('md'));

  // Validate that anchorEl is a valid element in the document
  const isValidAnchor = anchorEl && anchorEl instanceof Element && document.contains(anchorEl);

  // Check if there are any pending settlement requests
  const hasPendingSettlementRequests = pendingPayoutRequests.some(
    (request) => request.payoutType === 'settlement',
  );

  // Check if net balance is -100 or less (outstanding balance)
  const hasOutstandingBalance =
    netBalance !== undefined && netBalance !== null && netBalance <= -100;

  // Disable "Apply credit to fees" if there are pending requests OR outstanding balance
  const shouldDisableApplyCredit = hasPendingSettlementRequests || hasOutstandingBalance;

  const handleApplyCreditClick = (e) => {
    if (shouldDisableApplyCredit) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onApplyCreditClick(e);
  };

  return (
    <>
      {/* Custom backdrop with controlled z-index */}
      {isOpen && isValidAnchor && <Box className={classes.customBackdrop} onClick={onClose} />}
      <Popover
        isOpen={isOpen && isValidAnchor}
        onClose={onClose}
        anchorEl={isValidAnchor ? anchorEl : null}
        anchorOrigin={{
          vertical: breakpointMdDown ? 'bottom' : 'center',
          horizontal: breakpointMdDown ? 'center' : 'right',
        }}
        transformOrigin={{
          vertical: breakpointMdDown ? 'top' : 'center',
          horizontal: breakpointMdDown ? 'center' : 'left',
        }}
        style={{
          backgroundColor: 'transparent', // Remove default backdrop, we use custom one
        }}
        innerStyle={{
          padding: 0,
          backgroundColor: 'transparent',
          boxShadow: 'none',
          pointerEvents: 'auto',
          marginTop: breakpointMdDown ? '8px' : '4px',
          marginLeft: breakpointMdDown ? '0' : '32px',
          marginRight: breakpointMdDown ? '0' : 'auto',
          borderRadius: '24px',
          width: breakpointMdDown ? 'calc(100vw - 32px)' : 'auto',
          maxWidth: breakpointMdDown ? '382px' : 'none',
        }}
      >
        <Box className={classes.creditPopoverContainer} data-mobile={breakpointMdDown}>
          <Box className={classes.creditAmountsContainer}>
            <Box className={classes.creditBox}>
              <Box className={classes.creditAmount}>
                {formatCurrency(availableCredit, true, '-', false, true)}
              </Box>
              <Box className={classes.creditLabel}>Available Credit</Box>
            </Box>
            <Box className={classes.creditBox}>
              <Box className={classes.accruedAmount}>
                {formatCurrency(accruedCredit, true, '-', false, true)}
              </Box>
              <Box className={classes.accruedLabelContainer}>
                <Box className={classes.accruedLabel}>Accrued Credit</Box>
                <InfoTooltip
                  text='Accrued credit is earned after 8 weeks and becomes available weekly thereafter.'
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  offsetX={breakpointMdDown ? 230 : 230}
                  offsetY={breakpointMdDown ? -40 : -60}
                >
                  <Box className={classes.infoIconCircle}>i</Box>
                </InfoTooltip>
              </Box>
            </Box>
          </Box>
          <Divider />
          <Box className={classes.creditSectionTitle}>Spend your credit</Box>
          <Box className={classes.creditSectionContent}>
            <Box className={classes.creditListItem} onClick={onMarketBusinessClick}>
              <Box className={classes.creditIconBox}>
                <Box className={classes.creditIcon}>
                  <LightbulbSVG />
                </Box>
              </Box>
              <Box className={classes.creditItemContent}>
                <Box className={classes.creditItemTitle}>Market my business</Box>
                <Box className={classes.creditItemSubtext}>Marketing to 2 Mill + customers</Box>
              </Box>
              <RightArrowSVG className={classes.creditArrow} />
            </Box>
            {shouldDisableApplyCredit ? (
              <PopoverTooltip
                message={
                  hasOutstandingBalance
                    ? 'Credit cannot be applied while you have an outstanding balance.'
                    : 'Credit will be applied to your next settlement.\n\nPlease wait until the settlement is processed before applying additional credit.'
                }
                placement={breakpointMdDown ? 'top' : 'top'}
                anchorOrigin={{
                  vertical: breakpointMdDown ? 'top' : 'top',
                  horizontal: breakpointMdDown ? 'center' : 'right',
                }}
                transformOrigin={{
                  vertical: breakpointMdDown ? 'bottom' : 'bottom',
                  horizontal: breakpointMdDown ? 'center' : 'right',
                }}
                offsetX={breakpointMdDown ? 0 : 200}
                offsetY={breakpointMdDown ? -10 : -140}
                breakpointMdDown={breakpointMdDown}
              >
                <Box className={classes.creditListItemDisabled} onClick={handleApplyCreditClick}>
                  <Box className={classes.creditIconBox}>
                    <Box className={classes.creditIcon}>
                      <SettlementSVG />
                    </Box>
                  </Box>
                  <Box className={classes.creditItemContent}>
                    <Box className={classes.creditItemTitle}>Apply credit to fees</Box>
                    <Box className={classes.creditItemSubtext}>Applies to next settlement</Box>
                  </Box>
                  <RightArrowSVG className={classes.creditArrowDisabled} />
                  <Box className={classes.creditListItemOverlay} />
                </Box>
              </PopoverTooltip>
            ) : (
              <Box className={classes.creditListItem} onClick={onApplyCreditClick}>
                <Box className={classes.creditIconBox}>
                  <Box className={classes.creditIcon}>
                    <SettlementSVG />
                  </Box>
                </Box>
                <Box className={classes.creditItemContent}>
                  <Box className={classes.creditItemTitle}>Apply credit to fees</Box>
                  <Box className={classes.creditItemSubtext}>Applies to next settlement</Box>
                </Box>
                <RightArrowSVG className={classes.creditArrow} />
              </Box>
            )}
          </Box>

          <Divider />

          <Box className={classes.creditHistoryRow} onClick={onCreditHistoryClick}>
            <Box className={classes.creditHistoryText}>Credit history</Box>
            <RightArrowSVG className={classes.creditArrow} />
          </Box>
        </Box>
      </Popover>
    </>
  );
};

CreditPopover.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  anchorEl: PropTypes.shape({}),
  onMarketBusinessClick: PropTypes.func.isRequired,
  onApplyCreditClick: PropTypes.func.isRequired,
  onCreditHistoryClick: PropTypes.func.isRequired,
  availableCredit: PropTypes.string,
  accruedCredit: PropTypes.string,
  pendingPayoutRequests: PropTypes.arrayOf(
    PropTypes.shape({
      payoutType: PropTypes.string,
      requestedAmount: PropTypes.number,
      requestedDate: PropTypes.string,
    }),
  ),
  netBalance: PropTypes.number,
};

export default CreditPopover;
